import React, { useEffect, useMemo } from "react";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import LoadingButton from "@mui/lab/LoadingButton";
import CheckBox from "../../components/CheckBox";
import TextField from "@mui/material/TextField";

import { useDemographics } from "../../contexts/Demographics";
import { useState } from "react";
import { LOADING, SUCCESS } from "../../constants/common";
import { trackClickEvent } from "../../mixpanel";

const GenderDrawer = ({ onClose, open }) => {
  const [genders, setGenders] = useState([]);
  const [otherGender, setOtherGender] = useState("");
  const [genderError, setGenderError] = useState("");

  const {
    demographics,
    gendersList,
    fetchGendersStatus,
    fetchGendersList,
    editGenders,
    updateGendersStatus,
    fetchDemographics,
    handleState,
  } = useDemographics();

  const otherItem = gendersList.find((childItem) => childItem.name === "Other");
  const OTHER_GENDER_ID = otherItem?.id;

  // Check if "Other" is selected
  const isOtherSelected = useMemo(() => {
    return genders.includes(OTHER_GENDER_ID);
  }, [genders, OTHER_GENDER_ID]);

  // Validate if "Other" field has valid content when selected
  const isOtherFieldValid = useMemo(() => {
    if (!isOtherSelected) return true; // Not selected, so valid

    // Check if it meets validation criteria
    const trimmedValue = otherGender.trim();

    // Minimum length
    if (trimmedValue.length < 3) return false;

    // Maximum length
    if (trimmedValue.length > 30) return false;

    // Character validation - only letters, spaces, hyphens, and apostrophes
    if (/[0-9!@#$%^&*()_+=\[\]{};:"\\|,.<>\/?]/.test(trimmedValue))
      return false;

    return true;
  }, [isOtherSelected, otherGender]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    // Must have at least one gender selected
    if (genders.length === 0) return false;

    // If "Other" is selected, the field must be valid
    if (isOtherSelected && !isOtherFieldValid) return false;

    return true;
  }, [genders, isOtherSelected, isOtherFieldValid]);

  function handleClose() {
    setGenders([]);
    setOtherGender("");
    setGenderError("");
    onClose();
  }

  function handleSave() {
    // Prevent save if form is invalid
    if (!isFormValid) return;

    const isOtherSelected = genders.includes(OTHER_GENDER_ID) && otherGender;

    const payload = {
      gender_ids: genders,
      other_gender: isOtherSelected ? otherGender.trim() : null,
    };

    editGenders(payload);
    trackClickEvent("Updated Profile Information", {
      page: "Edit Profile",
      component: "Demographics Gender Drawer",
      button: "Save",
    });
  }

  useEffect(() => {
    if (open) fetchGendersList();
  }, [open]);

  useEffect(() => {
    if (updateGendersStatus === SUCCESS) {
      handleState({ updateGendersStatus: "" });
      fetchDemographics(true);
      handleClose();
    }
  }, [updateGendersStatus]);

  useEffect(() => {
    let tempGender = [];
    demographics?.user_gender?.genders?.map((item) => {
      let childItem = gendersList.find((childitem) => childitem.name === item);
      if (childItem) {
        tempGender.push(childItem.id);
      }
    });
    setGenders(tempGender);
    setOtherGender(demographics?.user_gender?.gender_other || "");
  }, [gendersList, open, demographics]);

  function handleCheckBox(ev) {
    const rawId = ev.target.name;
    const id = parseInt(rawId);

    setGenders((genders) => {
      let updatedGenders;

      if (genders?.length === 0) {
        updatedGenders = [...genders, id];
      } else if (!genders.includes(id)) {
        updatedGenders = [...genders, id];
      } else {
        updatedGenders = genders.filter((item) => item !== id);
      }

      return updatedGenders;
    });

    // Clear otherGender and error if "Other" is unchecked
    if (id === OTHER_GENDER_ID && !ev.target.checked) {
      setOtherGender("");
      setGenderError("");
    }
  }

  const validateOtherGender = (value) => {
    const trimmedValue = value.trim();

    // Clear error if empty (validation will happen on save/blur)
    if (!trimmedValue) {
      setGenderError("");
      return true;
    }

    // Check length
    if (trimmedValue.length < 3) {
      setGenderError("Minimum 3 characters required");
      return false;
    }

    if (trimmedValue.length > 30) {
      setGenderError("Maximum 30 characters allowed");
      return false;
    }

    // Check character validation
    if (/[0-9!@#$%^&*()_+=\[\]{};:"\\|,.<>\/?]/.test(trimmedValue)) {
      setGenderError(
        "Only letters, spaces, hyphens (-) and apostrophes (') are allowed",
      );
      return false;
    }

    setGenderError("");
    return true;
  };

  const handleOtherGenderChange = (e) => {
    const value = e.target.value;
    setOtherGender(value);

    // Only validate if we have content
    if (value.trim()) {
      validateOtherGender(value);
    }
  };

  const handleOtherGenderBlur = () => {
    validateOtherGender(otherGender);
  };

  return (
    <Drawer open={open} onClose={handleClose} anchor="right">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          pt: 8,
        }}
      >
        <Box pb={3} px={3}>
          <Typography variant="h4" mb={1}>
            Provider Demographics
          </Typography>
          <Typography variant="body1" mb={1}>
            Please select your gender.
          </Typography>
          <Divider sx={{ mb: 4 }} />
          <Grid container spacing={2.5}>
            {fetchGendersStatus === LOADING ? (
              <>
                {[...Array(6)].map((_, index) => (
                  <Grid item xs={6} key={index}>
                    <Skeleton animation="wave" />
                  </Grid>
                ))}
              </>
            ) : (
              <>
                {gendersList.map((item) => (
                  <Grid item xs={6} key={item.id}>
                    <CheckBox
                      label={item.name}
                      name={item.id}
                      onChange={handleCheckBox}
                      checked={genders.includes(item.id)}
                    />
                    {item.id === OTHER_GENDER_ID &&
                      genders.includes(OTHER_GENDER_ID) && (
                        <TextField
                          fullWidth
                          label="Please add gender"
                          value={otherGender}
                          onChange={handleOtherGenderChange}
                          onBlur={handleOtherGenderBlur}
                          error={!!genderError}
                          helperText={genderError}
                          inputProps={{
                            minLength: 3,
                            maxLength: 30,
                          }}
                          required
                          sx={{ mt: 1 }}
                        />
                      )}
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </Box>
        <Box
          sx={{
            boxShadow: "0px 0px 32px rgba(0, 0, 0, 0.08)",
            px: 1,
            pt: 3,
            pb: 5.625,
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "sticky",
            width: "100%",
            backgroundColor: "background.paper",
            bottom: 0,
            zIndex: 9,
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            sx={{
              color: "primary.main",
            }}
            size="large"
            fullWidth
            onClick={handleClose}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSave}
            loading={updateGendersStatus === LOADING}
            disabled={!isFormValid || updateGendersStatus === LOADING}
          >
            Save
          </LoadingButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default GenderDrawer;
